import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import HomeLogic from '../home/HomeLogic';

// Données structurées de la FAQ : à garder alignées sur la FAQ de maquettes/src/4_script.js.
const FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Mes données sont-elles en sécurité ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Avant le premier fichier, on signe un accord de confidentialité et un contrat de sous-traitance RGPD. Je travaille avec des accès en lecture seule, que vous créez et retirez vous-même : l'écriture n'est demandée que si l'outil modifie vos annonces, et le devis le précise. L'outil et la base sont hébergés en Europe, sur un compte à votre nom, et chacun n'y voit que ce qui le concerne. L'IA est une option : si vous l'activez, elle passe par votre compte professionnel Claude ou ChatGPT, qui n'entraîne pas ses modèles sur vos données, et ne lit que ce que votre rôle autorise. Sans votre accord, rien ne part vers une IA. En fin de mission, je supprime mes copies et je vous le confirme par écrit."
      }
    },
    {
      "@type": "Question",
      "name": "J'ai déjà Sellerboard, Prediko ou un outil d'analytics. Pourquoi vous ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Gardez-les. Je construis ce qu'ils ne font pas : la marge sur tous vos canaux à la fois, vos règles de coût, le rapprochement avec la compta. Et je les relie entre eux."
      }
    },
    {
      "@type": "Question",
      "name": "On a déjà un ERP. Vous le remplacez ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Non, je me branche dessus. Odoo, Sage, Cegid, votre caisse, Shopify, PrestaShop ou WooCommerce : votre ERP reste la référence pour les commandes et la compta. L'outil y lit ce dont il a besoin pour faire ce que l'ERP ne fait pas, comme le réappro multicanal, la planification de la production ou la marge par canal."
      }
    },
    {
      "@type": "Question",
      "name": "Amazon et Shopify ont leurs assistants IA. Ça ne suffit pas ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Chaque plateforme ne voit que ses propres données : ni vos coûts fournisseurs, ni vos autres canaux, ni votre compta. Je construis la couche qui manque, puis je la branche sur votre IA."
      }
    },
    {
      "@type": "Question",
      "name": "Combien ça coûte ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Tout est sur mesure. Un outil : à partir de 2 900 € HT. Votre système, plusieurs outils sur la même base : à partir de 7 500 € HT. La formation de votre équipe : 900 € HT la demi-journée, incluse dans votre système. Le prix est fixé par écrit avant de commencer, et se paie en deux fois : 50 % au démarrage, 50 % quand votre équipe a validé l'outil."
      }
    },
    {
      "@type": "Question",
      "name": "Et une fois l'outil livré ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Les corrections sont incluses pendant 30 jours, 60 pour votre système. Ensuite, l'outil reste à vous. Sans suivi, il tourne tant qu'Amazon et Shopify ne changent pas leurs formats ; s'ils changent, je corrige sur devis. Le suivi, de 300 à 600 € HT par mois selon le nombre de sources branchées, couvre ces changements et les petites évolutions, sans engagement. L'hébergement est à votre nom : son coût, en général quelques dizaines d'euros par mois, est indiqué dans le devis. Et pour ajouter un outil, on repart de la même base : vous ne payez que ce qui s'ajoute."
      }
    },
    {
      "@type": "Question",
      "name": "Mon équipe n'est pas technique. Saura-t-elle s'en servir ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Oui. Chaque outil est livré avec une prise en main sur vos propres données. La formation va plus loin : une demi-journée de montée en compétences sur les outils d'IA, appliquée à vos propres chiffres, pour que l'équipe interroge ses données avec Claude ou ChatGPT et sache vérifier les réponses. Elle est incluse dans votre système, et existe aussi seule, à 900 € HT : vous ne dépendez pas de moi pour chaque question."
      }
    },
    {
      "@type": "Question",
      "name": "Faudra-t-il encore exporter des fichiers chaque semaine ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Non. Amazon et Shopify se connectent directement à la base : ventes, stock et frais arrivent seuls. Pour le reste (fournisseurs, revendeurs, compta), je me branche sur votre logiciel quand c'est possible, sinon je reprends vos Excel tels qu'ils sont."
      }
    },
    {
      "@type": "Question",
      "name": "On n'a que des Excel. Par où on commence ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Par eux. Je reprends vos fichiers tels qu'ils sont, je les nettoie et je les range dans une seule base. Vos outils se construisent dessus, et personne n'a besoin d'équipe technique."
      }
    },
    {
      "@type": "Question",
      "name": "À qui appartient le code ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "À vous : code, données et documentation. N'importe quel développeur, ou votre équipe avec ses outils d'IA, peut le faire évoluer."
      }
    },
    {
      "@type": "Question",
      "name": "Et si vous n'êtes plus disponible ?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "L'outil ne dépend pas de moi. Le code, la base et la documentation sont à vous, hébergés sur un compte à votre nom. N'importe quel développeur, ou votre équipe avec ses outils d'IA, peut reprendre là où je m'arrête, et le suivi mensuel est sans engagement."
      }
    }
  ]
};

// La vidéo de démo du haut de page, décrite pour la recherche vidéo de Google.
// À mettre à jour si la vidéo est refaite (uploadDate avec son fuseau horaire, exigé par Google ; duration : 35 s aujourd'hui).
const VIDEO_LD = {
  "@context": "https://schema.org",
  "@type": "VideoObject",
  "name": "Démo Autoria : un fichier Excel devient un outil sur mesure",
  "description": "Démonstration de 35 secondes : un fichier Excel se transforme en outil sur mesure, puis quatre équipes e-commerce l'utilisent pour le réappro, la marge, la publicité et le catalogue.",
  "thumbnailUrl": ["https://autoria.dev/video/hero-poster.jpg"],
  "uploadDate": "2026-10-14T19:44:56+04:00",
  "duration": "PT35S",
  "contentUrl": "https://autoria.dev/video/hero.mp4",
  "inLanguage": "fr-FR",
  "width": 1280,
  "height": 720
};

// Page d'accueil : la maquette « Utilitaire » (maquettes/src), convertie par maquettes/outils/dc2react.py.
const HomePage = () => {
  useEffect(() => {
    const target = window.location.hash && document.getElementById(window.location.hash.slice(1));
    if (target) setTimeout(() => target.scrollIntoView(), 50);
    else window.scrollTo(0, 0);
    const previous = document.body.style.background;
    document.body.style.background = '#FBFBFA';
    return () => { document.body.style.background = previous; };
  }, []);

  return (
    <>
      <SEO
        title="Vos Excel en outils sur mesure pour l'e-commerce"
        description="Je transforme les Excel des équipes e-commerce en bases de données et en outils sur mesure : réappro, marge, pub, catalogue. Dès 2 900 € HT, en 3 semaines."
        path="/"
        imageAlt="Un fichier Excel transformé en outil sur mesure pour une équipe e-commerce"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
        <script type="application/ld+json">{JSON.stringify(VIDEO_LD)}</script>
      </Helmet>
      <HomeLogic />
    </>
  );
};

export default HomePage;
